import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import ProductSliderView from "../../../Components/Widgets/ProductListView";
import { resolveSectionSpacing, SECTION_MB } from "./common/section-spacing";

const SliderContainer = styled(Box)({
  display: "flex",
  overflowX: "auto",
  scrollSnapType: "x mandatory",
  width: "100%",
  maxWidth: "100vw",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
});

const Slide = styled(Box)(({ theme }) => ({
  flex: "0 0 68vw",
  scrollSnapAlign: "start",
  minWidth: 0,
  [theme.breakpoints.up("sm")]: {
    flex: "0 0 38vw",
  },
  [theme.breakpoints.up("md")]: {
    flex: "0 0 24vw",
  },
  [theme.breakpoints.up("lg")]: {
    flex: "0 0 20vw",
  },
  [theme.breakpoints.up("xl")]: {
    flex: "0 0 18vw",
  },
}));

const ArrowButton = styled(IconButton)({
  fontSize: 14,
  color: "#000",
  backgroundColor: "transparent",
  width: 32,
  height: 32,
  padding: 0,
  transition: "opacity 0.25s ease, transform 0.25s ease",
  "&:disabled": {
    backgroundColor: "transparent",
    opacity: 0.2,
  },
  "&:not(:disabled):hover": {
    backgroundColor: "transparent",
    opacity: 0.55,
    transform: "scale(1.05)",
  },
});

function SliderControls({ scrollProgress, isAtStart, isAtEnd, onScroll }) {
  return (
    <Box
      sx={{
        display: { xs: "none", md: "flex" },
        justifyContent: "center",
        mt: 5,
        px: 3,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2.5}
        sx={{ width: "100%", maxWidth: 1000 }}
      >
        <ArrowButton
          onClick={() => onScroll("left")}
          disabled={isAtStart}
          aria-label="Previous"
        >
          <i className="fa-sharp fa-regular fa-chevron-left" />
        </ArrowButton>

        <Box
          sx={{
            flex: 1,
            position: "relative",
            height: "1px",
            bgcolor: "rgba(0,0,0,0.12)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: `${scrollProgress}%`,
              bgcolor: "#000",
              transition: "width 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          />
        </Box>

        <ArrowButton
          onClick={() => onScroll("right")}
          disabled={isAtEnd}
          aria-label="Next"
        >
          <i className="fa-sharp fa-regular fa-chevron-right" />
        </ArrowButton>
      </Stack>
    </Box>
  );
}

const defaultProductDisplay = {
  showName: true,
  showCollection: true,
  showPrice: true,
  showGems: true,
  showCuration: true,
  enableImageSwipe: false,
};

function ProductSlideSkeleton() {
  return (
    <Box sx={{ width: "100%" }}>
      <Skeleton
        variant="rectangular"
        sx={{ width: "100%", aspectRatio: "4 / 5", borderRadius: 0 }}
      />
      <Stack spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
        <Skeleton variant="text" width="45%" height={14} />
        <Skeleton variant="text" width="70%" height={18} />
        <Skeleton variant="text" width="35%" height={14} />
      </Stack>
    </Box>
  );
}

export default function RecentlyViewed(props) {
  const { component } = props;
  const theme = useTheme();
  const sliderRef = useRef(null);

  const {
    title = "RECENTLY VIEWED",
    desc = "",
    descMaxWidth = "560px",
    url = "",
    productDisplay = {},
    mt,
    mb,
  } = component.props;

  const { mt: sectionMt, mb: sectionMb } = resolveSectionSpacing(
    { mt, mb },
    { mb: SECTION_MB.standard },
  );

  const { products = [], productsLoaded = false } = component.getData(
    "default",
    {},
  );

  const display = { ...defaultProductDisplay, ...productDisplay };

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const checkScrollPosition = () => {
    if (!sliderRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
    const totalVisible = scrollLeft + clientWidth;
    const progress =
      scrollWidth > clientWidth ? (totalVisible / scrollWidth) * 100 : 100;

    setScrollProgress(progress);
    setIsAtStart(scrollLeft <= 1);
    setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
  };

  const scrollSlider = (direction) => {
    if (!sliderRef.current) return;

    sliderRef.current.scrollBy({
      left:
        direction === "left"
          ? -sliderRef.current.offsetWidth * 0.75
          : sliderRef.current.offsetWidth * 0.75,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider || !productsLoaded || products.length === 0) return undefined;

    slider.scrollLeft = 0;

    const handleScroll = () => checkScrollPosition();
    slider.addEventListener("scroll", handleScroll, { passive: true });

    const timer = setTimeout(checkScrollPosition, 0);

    return () => {
      slider.removeEventListener("scroll", handleScroll);
      clearTimeout(timer);
    };
  }, [products, productsLoaded]);

  if (productsLoaded && products.length === 0) {
    return null;
  }

  const hasHeader = Boolean(title?.trim()) || Boolean(desc?.trim());
  const isLoading = !productsLoaded;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        mt: sectionMt,
        pb: sectionMb,
        overflow: "hidden",
      }}
    >
      {hasHeader && (
        <Container
          maxWidth="md"
          sx={{ px: { xs: 2, md: 3 }, mb: { xs: 4, md: 5 } }}
        >
          <Stack
            spacing={{ xs: 2, md: 2.5 }}
            alignItems="center"
            textAlign="center"
          >
            {title?.trim() && (
              <Typography
                component={url ? RouterLink : "h2"}
                to={url || undefined}
                sx={{
                  ...theme.custom.titles.title,
                  textDecoration: "none",
                  display: "block",
                }}
              >
                {title}
              </Typography>
            )}

            {desc?.trim() && (
              <Typography
                component="p"
                sx={{
                  ...theme.custom.titles.description,
                  maxWidth: descMaxWidth,
                  mx: "auto",
                  px: 0,
                }}
              >
                {desc}
              </Typography>
            )}
          </Stack>
        </Container>
      )}

      <SliderContainer ref={sliderRef}>
        <Stack direction="row" spacing={{ xs: 1.5, md: 2 }} sx={{ pt: 2 }}>
          {isLoading &&
            Array.from({ length: 4 }).map((_, index) => (
              <Slide key={`recently-viewed-skeleton-${index}`}>
                <ProductSlideSkeleton />
              </Slide>
            ))}

          {!isLoading &&
            products.map((product) => (
              <Slide key={product._id}>
                <ProductSliderView
                  {...props}
                  product={product}
                  productDisplay={display}
                />
              </Slide>
            ))}
        </Stack>
      </SliderContainer>

      {!isLoading && products.length > 0 && (
        <SliderControls
          scrollProgress={scrollProgress}
          isAtStart={isAtStart}
          isAtEnd={isAtEnd}
          onScroll={scrollSlider}
        />
      )}
    </Box>
  );
}
